import { useState, useEffect } from "react"
import cx from "classnames"

import NumberInput from "@/components/forms/NumberInput"
import PowerToggle from "@/components/forms/PowerToggle"
import Label from "@/components/forms/Label"
import FilterTypeSelect from "@/components/forms/FilterTypeSelect"

// Define the missing type
type FilterType = "lowshelf" | "peaking" | "highshelf"

type EqualizerTableProps = {
  eqSettings: any
  onEqChange: (changes: any[]) => void
  mode: string
  cachedGains?: Record<string, number>
  onCacheGain?: (bandName: string, gain: number) => void
  onClearCache?: (bandName: string) => void
}

const EqualizerTable = ({
  eqSettings,
  onEqChange,
  mode,
  cachedGains = {},
  onCacheGain,
  onClearCache,
}: EqualizerTableProps) => {
  const [bands, setBands] = useState([])
  const [filterTypes, setFilterTypes] = useState<Record<string, FilterType>>({})

  /**
   * On settings change
   *
   * When parent settings change, update the bands
   */
  useEffect(() => {
    if (eqSettings) {
      // Log all keys in eqSettings for debugging
      //console.log("eqSettings keys:", Object.keys(eqSettings));

      const filteredBands = Object.entries(eqSettings).filter(
        ([key, value]) =>
          key !== "Preamp" &&
          key !== "Crossfeed" &&
          !key.startsWith("Loudness") &&
          !key.includes("Loudness")
      )
      //console.log("Filtered bands length:", filteredBands.length);
      //console.log("Filtered bands keys:", filteredBands.map(([key]) => key));

      setBands(filteredBands)

      // Initialize filter types from device data if available
      const initialFilterTypes: Record<string, FilterType> = {}
      filteredBands.forEach(([bandName, bandData]) => {
        // Check if Type is provided in the band data
        if (bandData["Type"] && bandData["Type"]["Current"]) {
          // Convert to lowercase to match our FilterType type
          const deviceType = bandData["Type"]["Current"].toLowerCase() as FilterType
          initialFilterTypes[bandName] = deviceType
        } else {
          // Fallback to peaking if no type data is available (for backward compatibility)
          initialFilterTypes[bandName] = "peaking"
        }
      })
      setFilterTypes((prev) => ({ ...prev, ...initialFilterTypes }))
    }
  }, [eqSettings, mode])

  /**
   * Update
   *
   * @param {*} band
   * @param {*} domain
   * @param {*} value
   * @returns
   */
  const update = async (band: [string, any], domain: string, value: number | string) => {
    const bandName = band[0]
    // Call onEqChange and consider it a success by default
    await onEqChange([[bandName, domain, value]])
    return true // Assume success for now
  }

  /**
   * Disable a band, caching its current gain value
   */
  const disableBand = async (band: [string, any]) => {
    const bandName = band[0]
    const currentGain = parseFloat(band[1]["Gain"]["Current"])

    // Cache the current gain before disabling
    if (onCacheGain) {
      onCacheGain(bandName, currentGain)
    }

    // Return the result of the update operation
    return await update(band, "Gain", 0)
  }

  const enableBand = async (band: [string, any]) => {
    const bandName = band[0]
    const cachedGain = cachedGains[bandName]

    // Only proceed if we have a cached value
    if (typeof cachedGain === "number") {
      // Apply the cached gain value
      const result = await update(band, "Gain", cachedGain)

      // Clear cached gain after successful update
      if (result && onClearCache) {
        onClearCache(bandName)
      }

      return result
    }
    return false
  }

  const updateFilterType = async (band: [string, any], newType: FilterType) => {
    const bandName = band[0]

    // Update local state first
    setFilterTypes((prev) => ({ ...prev, [bandName]: newType }))

    // Convert UI filter type to device format (capitalize)
    const deviceTypeValue = newType.toUpperCase()

    // Log the filter type change for debugging
    console.log(`Filter type change for ${bandName}: ${newType} (${deviceTypeValue})`)

    // Check if we're switching to a shelf filter
    const isShelfFilter = newType === "lowshelf" || newType === "highshelf"
    const currentQ = parseFloat(band[1]["Q"]["Current"])

    // If switching to a shelf filter and Q > 3, we need to reset Q to 0.707
    if (isShelfFilter && currentQ > 3) {
      console.log(
        `Resetting Q value from ${currentQ} to 0.707 due to filter type change to ${newType}`
      )

      // Send both updates to the device
      await onEqChange([
        [bandName, "Type", deviceTypeValue],
        [bandName, "Q", 0.707],
      ])

      return true
    }

    // Otherwise, just update the filter type
    return await update(band, "Type", deviceTypeValue)
  }

  return (
    <div className="min-w-300">
      {bands.length && (
        <div
          className="grid gap-x-5 gap-y-1"
          style={{ gridTemplateColumns: `auto repeat(${bands.length}, 1fr)` }}
        >
          {bands.map((band, i) => {
            const domain = band[1]["Gain"]
            const value = parseFloat(domain["Current"])
            const isActive = value !== 0.0
            const bandName = band[0]
            const hasCache = typeof cachedGains[bandName] === "number"
            // Check if the device supports filter types
            const deviceSupportsFilterTypes = bands.some(
              (b) => b[1]["Type"] && b[1]["Type"]["Current"]
            )

            return (
              <Band
                key={`label-${i}`}
                name={bandName}
                number={i + 1}
                isActive={isActive}
                canEnable={!isActive && hasCache}
                showFilterType={!deviceSupportsFilterTypes}
                onToggle={async () => {
                  if (isActive) {
                    await disableBand(band)
                  } else {
                    await enableBand(band)
                  }
                }}
              />
            )
          })}

          {/* Only render the Type row if at least one band has Type information */}
          {bands.some((band) => band[1]["Type"] && band[1]["Type"]["Current"]) && (
            <>
              <Label text="Type" />
              {bands.map((band, i) => {
                const bandName = band[0]
                const currentType = filterTypes[bandName] || "peaking"
                return (
                  <FilterTypeSelect
                    key={`type-${i}`}
                    value={currentType}
                    onChange={(value) => updateFilterType(band, value)}
                  />
                )
              })}
            </>
          )}

          <Label text="Gain" />
          {bands.map((band, i) => {
            const domain = band[1]["Gain"]
            const value = parseFloat(domain["Current"])
            const min = parseFloat(domain["Min"])
            const max = parseFloat(domain["Max"])
            return (
              <NumberInput
                key={`gain-${i}-${value}`}
                value={value}
                tabIndex={i * 3 + 1}
                min={min}
                max={max}
                step={0.1}
                disabled={false}
                onDebouncedChange={(value) => {
                  return update(band, "Gain", value)
                }}
              />
            )
          })}
          <Label text="Freq" />
          {bands.map((band, i) => {
            const domain = band[1]["Frequency"]
            const value = parseFloat(domain["Current"])
            const min = parseFloat(domain["Min"])
            const max = parseFloat(domain["Max"])
            return (
              <NumberInput
                key={`freq-${i}-${value}`}
                value={value}
                tabIndex={i * 3 + 2}
                min={min}
                max={max}
                disabled={false}
                onDebouncedChange={(value) => {
                  return update(band, "Frequency", value)
                }}
              />
            )
          })}
          <Label
            text="Q"
            info={
              <div className="max-w-md space-y-1">
                <p>
                  Controls the width of the frequency range affected by the filter. A higher Q
                  focuses on a narrower range, while a lower Q affects a wider range.
                </p>
                <p>
                  Note: Q values for shelf filters (lowshelf/highshelf) are sent to Element IV, but
                  are not visually rendered above.
                </p>
              </div>
            }
          />
          {bands.map((band, i) => {
            const domain = band[1]["Q"]
            const value = parseFloat(domain["Current"])
            const bandName = band[0]
            const currentFilterType = filterTypes[bandName] || "peaking"

            // Get the basic min/max from the band data
            let min = parseFloat(domain["Min"])
            let max = parseFloat(domain["Max"])

            // Override with filter-type specific limits if the type has changed
            if (currentFilterType === "peaking") {
              // For peaking filters, allow higher Q values
              max = Math.max(max, 50) // Use 50 as max Q for peaking filters
            } else if (currentFilterType === "lowshelf" || currentFilterType === "highshelf") {
              // For shelf filters, use a lower max Q
              max = Math.min(max, 3.0) // Use 3.0 as max Q for shelf filters
            }

            return (
              <NumberInput
                key={`q-${i}-${value}-${currentFilterType}`}
                value={value}
                tabIndex={i * 3 + 3}
                min={min}
                max={max}
                step={0.01}
                disabled={false}
                onDebouncedChange={(value) => {
                  return update(band, "Q", value)
                }}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Renders text above the band number
 * Firmware will eventually support dynamic `filter_type` and this function will be removed.
 */
const Band = ({ className = "", name, number, isActive, canEnable, showFilterType, onToggle }) => {
  const getDisplayName = (name: string) => {
    // Define filter types to simplify to their base names
    const filterPrefixes = ["Peaking", "Lowshelf", "Highshelf"]

    // Check if name starts with any of the prefixes
    for (const prefix of filterPrefixes) {
      if (name.startsWith(prefix)) {
        return prefix
      }
    }

    // For all other cases, use the full name as is
    return name
  }

  return (
    <div className={cx("pb-3", number === 1 ? "col-start-2" : "", className)}>
      {/* When showing filter type, display it above the band number */}
      {showFilterType && <div className="type-body-xs font-medium">{getDisplayName(name)}</div>}

      <div className="flex items-center font-ibm text-xs font-bold">
        Band {number}
        <PowerToggle isActive={isActive} canEnable={canEnable} disable={onToggle} />
      </div>
    </div>
  )
}

export default EqualizerTable
